import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import eligibilityCheckServices from "../../../../services/api/service/eligibilityCheckService";
import { mapCollateral } from "../../../../utils/helpers";
export function useDocumentsData({
  tabIndex,
  applicants,
  formUserData,
  applicationNumber,
  partnerStartedJourney,
  coApplicantDetails,
  documentApplicantType,
  fetchUserApplicationData,
  isAuthenticated,
  useMandatoryDocumentsOnly = false,
}: {
  tabIndex: number;
  applicants: string[];
  formUserData: any;
  applicationNumber: any;
  partnerStartedJourney: boolean;
  coApplicantDetails: any;
  documentApplicantType: string;
  fetchUserApplicationData: any;
  isAuthenticated: boolean;
  useMandatoryDocumentsOnly?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [documentData, setDocumentData] = useState<any>([]);
  const [hasNotRequiredDocument, setHasNotRequiredDocument] = useState<any>([]);

  const [documentCategoryId, setDocumentCategoryId] = useState<number>(0);
  const [documentCategories, setDocumentCategories] = useState<any>([]);
  const [masterDocumentList, setMasterDocumentList] = useState<any>([]);
  const lastAutoFetchKeyRef = useRef<string>("");

  const findCoApplicantNumber: number = useMemo(() => {
    return Number(documentApplicantType?.split(" ")?.[1]);
  }, [documentApplicantType]);

  // Derive co-applicant employment type and pay party from application data (same source as Summary handleFetchApplicationDetail)
  const { coApplicantEmploymentType, isPayPartyValue } = useMemo(() => {
    let employmentTypeValue = "";
    let employmentIsEmployedValue = "";
    let payPartyValue = "";

    if (tabIndex <= 0 || !fetchUserApplicationData?.horizontalSteps) {
      return { coApplicantEmploymentType: "", isPayPartyValue: "" };
    }

    const employmentTypeKey = `employmentCoApplicant${findCoApplicantNumber}employmentType`;
    const payPartyKey = `basicCoApplicant${findCoApplicantNumber}PayParty`;
    const employmentIsEmployedKey = `employmentCoApplicant${findCoApplicantNumber}isEmployed`;

    for (let i = 0; i < fetchUserApplicationData.horizontalSteps.length; i++) {
      const verticalSteps = fetchUserApplicationData.horizontalSteps[i]?.verticalSteps || [];
      for (let j = 0; j < verticalSteps.length; j++) {
        const fields = verticalSteps[j]?.fields || [];
        const employmentIsEmployedField = fields.find((item: any) => {
          const key = Object.keys(item)[0];
          return key === employmentIsEmployedKey;
        });
        const employmentTypeField = fields.find((item: any) => {
          const key = Object.keys(item)[0];
          return key === employmentTypeKey;
        });
        if (employmentTypeField) {
          employmentTypeValue = Object.values(employmentTypeField)[0] as string;
          employmentIsEmployedValue = Object.values(employmentIsEmployedField || {})[0] as string;

          const payPartyField = fields.find((item: any) => {
            const key = Object.keys(item)[0];
            return key === payPartyKey;
          });

          if (payPartyField) {
            payPartyValue = Object.values(payPartyField)[0] as string;
          }
          break;
        }
      }
      if (employmentTypeValue) break;
    }

    const coApplicantEmploymentTypeRes = employmentIsEmployedValue === "Yes" ? employmentTypeValue : "";
    return { coApplicantEmploymentType: coApplicantEmploymentTypeRes, isPayPartyValue: payPartyValue };
  }, [tabIndex, findCoApplicantNumber, fetchUserApplicationData]);

  const formatApplicantType = useCallback((value: string) => {
    return value
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase())
      .replace(/Co Applicant/i, "Co-Applicant");
  }, []);

  const getDocumentCategoriesViaAPI = useCallback(async () => {
    try {
       const examNames = Object.keys(formUserData).filter((key) => {
          // Match:
          // - languageExamName
          // - languageExamName_1
          // - coreExamName
          // - coreExamName_1
          const match = key.match(
            /^(languageExamName|coreExamName)(?:_(\d+))?$/
          );

          if (!match) return false;

          const examType = match[1]; // languageExamName | coreExamName
          const index = match[2]; // undefined | "1" | "2" ...

          // Determine corresponding score field
          const scoreKey = index
            ? `${examType.replace("Name", "Score")}_${index}`
            : examType.replace("Name", "Score");

          const scoreValue =
            formUserData[
              scoreKey as keyof typeof formUserData
            ];

          // Include exam only if corresponding score exists
          return (
            scoreValue !== null &&
            scoreValue !== undefined &&
            scoreValue !== ""
          );
        })
        .map(
          (key) =>
            formUserData[
              key as keyof typeof formUserData
            ]
        )
        .filter(Boolean);
      let collateralTypeData=mapCollateral().filter((item:any)=>item.value === formUserData?.collateralType)?.[0]?.label || formUserData?.collateralType;
      if (tabIndex < 0) return;
      if (partnerStartedJourney === (null as any)) return;

      setIsLoading(true);

      const payload:any = partnerStartedJourney
        ? {
            applicationId: fetchUserApplicationData?.applicationId,
            applicantId:
              tabIndex === 0
                ? fetchUserApplicationData?.applicantId
                : String(
                    coApplicantDetails?.[
                      `coapplicant${findCoApplicantNumber}` as keyof typeof coApplicantDetails
                    ]?.coApplicantId ?? ""
                  ),
            trackingId: String(applicationNumber ?? ""),
            applicantType:
              tabIndex === 0
                ? "Applicant"
                : formatApplicantType(
                    String(
                      coApplicantDetails?.[
                        `coapplicant${findCoApplicantNumber}` as keyof typeof coApplicantDetails
                      ]?.coApplicantType ?? ""
                    )
                  ),
             ...(tabIndex === 0 && {
                isCollateral:
                  formUserData?.collateral || "No",
                isAppliedEntranceExam:
                  formUserData?.coreExamApplied==="Yes" || 
                  formUserData?.languageExamApplied==="Yes" ?"Yes":"No",
                collateralType: collateralTypeData?.toLowerCase(),
                entranceExamNames: examNames,
              }),
          }
        : {
            productType: formUserData?.product,
            typeOfApplicant: tabIndex === 0 ? "Applicant" : applicants?.[tabIndex],
            productCategory: formUserData?.courseCategory,
            typeOfIndividual: "Individual",
            employmentCategory: tabIndex == 0 ? formUserData?.employmentType || formUserData?.applicantType : formUserData?.[`employmentCoApplicant${applicants?.[tabIndex]?.split(" ")?.[1]}employmentType`] || formUserData?.applicantType,
            trackingId: applicationNumber,
            ...(tabIndex === 0 && {
                isCollateral:
                  formUserData?.collateral || "No",
                isAppliedEntranceExam:
                  formUserData?.coreExamApplied==="Yes" || 
                  formUserData?.languageExamApplied==="Yes" ?"Yes":"No",
                collateralType: collateralTypeData?.toLowerCase(),
                entranceExamNames: examNames,
              }),
          };

      let response: any;
      if (useMandatoryDocumentsOnly) {
        response = await eligibilityCheckServices.getMandatoryDocuments({
          productType: formUserData?.product,
          typeOfApplicant: tabIndex === 0 ? "Applicant" : applicants?.[tabIndex],
          productCategory: formUserData?.courseCategory,
          typeOfIndividual: "Individual",
          employmentCategory:
            tabIndex == 0
              ? formUserData?.employmentType || formUserData?.applicantType
              : formUserData?.[
                  `employmentCoApplicant${applicants?.[tabIndex]?.split(" ")?.[1]}employmentType`
                ] || formUserData?.applicantType,
          trackingId: applicationNumber,
          ...(tabIndex === 0 && {
                isCollateral:
                  formUserData?.collateral || "No",
                isAppliedEntranceExam:
                  formUserData?.coreExamApplied==="Yes" || 
                  formUserData?.languageExamApplied==="Yes" ?"Yes":"No",
                collateralType: collateralTypeData?.toLowerCase(),
                entranceExamNames: examNames,
              }),
        });
      } else if (partnerStartedJourney === true) {
        const hasValidIds =
          Boolean(payload.applicationId) && Boolean((payload as any).applicantId);
        if (!hasValidIds) {
          // Missing IDs in partner journey; avoid calling API
          setIsLoading(false);
          return;
        }
        response = await eligibilityCheckServices.getDocuments(payload);
      } else {
        response = await eligibilityCheckServices.getMandatoryDocuments(payload);
      }

      if (response?.status === 200) {
        const respData = response?.data?.data ?? [];
        const arrayOfData: any[] = [];
        const hasNotRequiredDocumentData: any[] = [];
        respData.forEach((item: any) => {
          const documentList = item?.documents;
   
          if (tabIndex > 0) {
            if (item?.name == "Academic Documents" || item?.name == "Entrance Exam Documents" || item?.name == "Proof of Admission") {
              item.isMand = false;
            }
            
            // Process documents based on employment type in a single loop
            documentList?.forEach((doc: any) => {
              // Helper function to update document properties
             const updateDocument = (doc: any, isMatch: boolean, isMandatory: boolean) => {
                if (isMatch) {
                  doc.byDefault = true;
                  doc.isMandatory = isMandatory;
                }
              };
               const match = doc?.typeOfApplicant?.match(/\d+/);
              const findApplicantIndex = match ? parseInt(match[0], 10) : null;
              let coapplicantEmploytype=formUserData?.[`employmentCoApplicant${findApplicantIndex}employmentType`];
              let basicCoAppPayparty=formUserData?.[`basicCoApplicant${findApplicantIndex}PayParty`];
              
              console.log(coapplicantEmploytype,basicCoAppPayparty,"formUserData",coApplicantEmploymentType,formUserData?.[`employmentCoApplicant${findApplicantIndex}employmentType`])
              // Handle passport documents
              if (doc?.docNameDms === "passport") {
                doc.isMandatory = false;
                doc.byDefault = false;
                return;
              }
              if (basicCoAppPayparty == "Yes") {
                const isBankStatement = doc?.docName.includes("Bank Statement") || doc?.docNameDms == "account_statement";

                updateDocument(doc, isBankStatement, true);
              } 
              if (doc?.docNameDms === "offer_letter" && (formUserData?.admissionStatus?.toLowerCase()==="undecided" || formUserData?.admissionStatus?.toLowerCase()==="applied")) {
                doc.isMandatory = false;
                doc.byDefault = true;
                return;
              }
              else {
                updateDocument(doc, false, false);
              }

              // Handle documents based on employment type
              if (coapplicantEmploytype == "Salaried") {
                const isSalarySlip = doc?.docName.includes("Salary Slips") || doc?.docNameDms == "salary_slip";
                updateDocument(doc, isSalarySlip, basicCoAppPayparty == "Yes" ? true : false);
              } else if (coapplicantEmploytype == "SENP") {
                const isItrOrGst = (doc?.docName.includes("ITR") || doc?.docNameDms == "itr") ||
                  (doc?.docName.includes("GST") || doc?.docNameDms == "business_proof");
                updateDocument(doc, isItrOrGst, basicCoAppPayparty == "Yes" ? true : false);
              } else if (coapplicantEmploytype == "SEP") {
                const isItr = doc?.docName.includes("ITR") || doc?.docNameDms == "itr";
                updateDocument(doc, isItr, basicCoAppPayparty == "Yes" ? true : false);
              } else if (coapplicantEmploytype == "Farmer" || coapplicantEmploytype == "Others") {
                const isBankStatement = doc?.docName.includes("Bank Statement") || doc?.docNameDms == "account_statement";
                if (basicCoAppPayparty == "Yes") {
                  updateDocument(doc, isBankStatement, true);
                } else {
                  updateDocument(doc, isBankStatement, false);
                }
              }
            });

          }
          if (tabIndex === 0) {
            
            // Process documents based on employment type in a single loop
            documentList?.forEach((doc: any) => {
              // Helper function to update document properties
              const updateDocument = (doc: any, isMatch: boolean, isMandatory: boolean) => {
                if (isMatch) {
                  doc.byDefault = true;
                  doc.isMandatory = isMandatory;
                }
              };
              if (doc?.docNameDms === "offer_letter" && (formUserData?.admissionStatus?.toLowerCase()==="undecided" || formUserData?.admissionStatus?.toLowerCase()==="applied")) {
                doc.isMandatory = false;
                doc.byDefault = false;
              }
              if(formUserData?.admissionStatus?.toLowerCase()==="confirmed" && doc?.docName === "I-20"){
                doc.isMandatory = false;
                doc.byDefault = false;
              }
              else if (formUserData?.employmentType == "Salaried" && doc?.docNameDms == "salary_slip") {
                const isSalarySlip = doc?.docName.includes("Salary Slips") || doc?.docNameDms == "salary_slip" ;
                updateDocument(doc, isSalarySlip, true);
              } else if ((formUserData?.employmentType == "Salaried" || formUserData?.employmentType === "SEP" || formUserData?.employmentType === "SENP"|| formUserData?.employmentType === "Others" || formUserData?.employmentType === "Farmer") && doc?.docName === "Bank Statement") {
                updateDocument(doc, true, true);
              }
              else if (formUserData?.employmentType == "SENP" && (doc?.docNameDms == "salary_slip" || doc?.docNameDms == "business_proof")) {
               updateDocument(doc, true, true);
              } else if (formUserData?.employmentType == "SEP" && doc?.docNameDms == "salary_slip") {
                const isItr = doc?.docName.includes("Proof of Income (Salary Slips/ITR)") || doc?.docNameDms == "salary_slip";
                //isPayPartyValue == "Yes" ? true : false
                updateDocument(doc, isItr, true);
              } else if (formUserData?.employmentType == "Farmer" || formUserData?.employmentType == "Others") {
                const isBankStatement = doc?.docName.includes("Bank Statement") || doc?.docNameDms == "account_statement";
                if (isPayPartyValue == "Yes") {
                  updateDocument(doc, isBankStatement, true);
                } else {
                  updateDocument(doc, isBankStatement, false);
                }
              }
              // if(doc?.docName === "Bank Statement"){
              //   doc.isMandatory = true;
              //   doc.byDefault = true;
              // }

              
            });

          }
          const hasRequiredDocument: any[] = documentList?.filter(
            (doc: any) =>
              doc?.byDefault || doc?.isMandatory || doc?.userDocs?.length > 0
          );
          const hasNotRequiredDocument: any[] = documentList?.filter(
            (doc: any) => !doc?.byDefault && !doc?.isMandatory
          );

          item.documents = hasRequiredDocument;

          if (tabIndex > 0) {
            if (item?.isMand) {
              arrayOfData.push(item);
            }
          } else {
            arrayOfData.push(item);
          }

          if (hasNotRequiredDocument.length > 0) {
            hasNotRequiredDocumentData.push(...hasNotRequiredDocument);
          }
        });

        setDocumentData(arrayOfData);
        setHasNotRequiredDocument(hasNotRequiredDocumentData);
      }
    } catch {
      setDocumentData([]);
      setHasNotRequiredDocument([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    tabIndex,
    partnerStartedJourney,
    fetchUserApplicationData,
    coApplicantDetails,
    findCoApplicantNumber,
    applicationNumber,
    applicants,
    formUserData,
    formatApplicantType,
    coApplicantEmploymentType,
    isPayPartyValue,
    useMandatoryDocumentsOnly,
  ]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!String(applicationNumber ?? "").trim()) return;
    if (partnerStartedJourney === null || partnerStartedJourney === undefined) {
      return;
    }
    if (
      partnerStartedJourney === true &&
      (!fetchUserApplicationData?.applicationId ||
        !fetchUserApplicationData?.applicantId)
    ) {
      return;
    }

    // Prevent re-fetch on plain form-data saves; auto-fetch only on tab/applicant context changes.
    const currentFetchKey = [
      tabIndex,
      documentApplicantType,
      applicationNumber,
      partnerStartedJourney === true ? "partner" : "non-partner",
      fetchUserApplicationData?.applicationId || "",
      fetchUserApplicationData?.applicantId || "",
    ].join("|");

    if (lastAutoFetchKeyRef.current === currentFetchKey) return;
    lastAutoFetchKeyRef.current = currentFetchKey;
    getDocumentCategoriesViaAPI();
  }, [
    isAuthenticated,
    tabIndex,
    documentApplicantType,
    applicationNumber,
    partnerStartedJourney,
    fetchUserApplicationData?.applicationId,
    fetchUserApplicationData?.applicantId,
    getDocumentCategoriesViaAPI,
  ]);

  const handleGetMasterDocumentList = useCallback(
    async (documentCategoryId: number) => {
      const payload = {
        productType: formUserData?.product || "SLI",
        typeOfApplicant: tabIndex === 0 ? "Applicant" : applicants?.[tabIndex],
        status: "active",
        documentCategoryId,
        employmentCategory: tabIndex == 0 ? formUserData?.employmentType || formUserData?.applicantType : formUserData?.[`employmentCoApplicant${applicants?.[tabIndex]?.split(" ")?.[1]}employmentType`] || formUserData?.applicantType,
      };
      const response = await eligibilityCheckServices.getDocumentList(payload);
      if (response?.status === 200) {
        const arrayOfObject: any[] = [
          { value: "", label: "Select Document" },
          ...(response?.data?.data ?? []).map((item: any) => ({
            value: item?.id,
            label: item?.docName,
          })),
          { value: "Others", label: "Others" },
        ];
        setMasterDocumentList(arrayOfObject);
      }
    },
    [formUserData, tabIndex]
  );

  const handleGetCategoryList = useCallback(
    async (id: number) => {
      setDocumentCategoryId(id);
      const response = await eligibilityCheckServices.getDocumentCategoriesApi();
      if (response?.status === 200) {
        const arrayOfObject: any[] = [
          { value: "", label: "Select Document Category" },
          ...(response?.data?.data ?? []).map((item: any) => ({
            value: item?.id,
            label: item?.name,
          })),
        ];

        setDocumentCategories(arrayOfObject);
        if (id !== 0) {
          await handleGetMasterDocumentList(id);
        }
      }
    },
    [handleGetMasterDocumentList]
  );

  return {
    isLoading,
    documentData,
    hasNotRequiredDocument,
    setDocumentData,
    setHasNotRequiredDocument,
    getDocumentCategoriesViaAPI,
    documentCategoryId,
    documentCategories,
    masterDocumentList,
    setMasterDocumentList,
    handleGetCategoryList,
    handleGetMasterDocumentList,
    setDocumentCategoryId,
    setDocumentCategories,
  };
}
